<template>
    <div class="popover" ref="root" :data-open="open">
        <span class="popover__trigger" ref="trigger" @click.stop="toggle">
            <slot name="trigger"></slot>
        </span>

        <div
            v-show="open"
            ref="content"
            class="popover__content"
            :style="contentStyle"
            role="dialog"
            aria-modal="false"
        >
            <flatIconButton class="button__close" :iconSize="16" @click="close">
                <CloseIconSvg slot="svgIcon" />
            </flatIconButton>
            <slot name="content"></slot>
        </div>
    </div>
</template>

<script>

import CloseIconSvg from '@/assets/icons/close.svg';
import flatIconButton from '@/components/ui/button/flatIconButton.vue';

export default {
    name: 'popoverUI',
    components: {
        CloseIconSvg,
        flatIconButton
    },
    props: {
        horizontalPlacement: {
            type: String,
            default: 'center', // 'left' | 'center' | 'right'
            validator: (v) => ['left', 'center', 'right'].includes(v),
        },
        placement: {
            type: String,
            default: 'bottom', // 'bottom' | 'top'
            validator: (v) => ['bottom', 'top'].includes(v),
        },
        offset: {
            type: Number,
            default: 4,
        },
        zIndex: {
            type: Number,
            default: 1000,
        },
    },
    data() {
        return {
            open: false,
            contentStyle: {
                top: '0px',
                left: '0px',
                zIndex: 1000,
            },
        }
    },
    mounted() {
        document.addEventListener('keydown', this.onKeyDown)
    },
    beforeDestroy() {
        document.removeEventListener('keydown', this.onKeyDown)
        this.removeGlobalListeners()
    },
    methods: {
        toggle() {
            if (this.open) {
                this.close()
            } else {
                this.openPopover()
            }
        },
        openPopover() {
            this.open = true
            this.$nextTick(() => {
                this.updatePosition()
                this.addGlobalListeners()
            })
        },
        close() {
            this.open = false
            this.removeGlobalListeners()
        },
        updatePosition() {
            if (!this.$refs.root || !this.$refs.trigger || !this.$refs.content) return

            const rootRect = this.$refs.root.getBoundingClientRect()
            const triggerRect = this.$refs.trigger.getBoundingClientRect()
            const contentRect = this.$refs.content.getBoundingClientRect()

            // Horizontal alignment relative to the trigger.
            let left
            if (this.horizontalPlacement === 'left') {
                left = triggerRect.left - rootRect.left
            } else if (this.horizontalPlacement === 'right') {
                left = triggerRect.right - contentRect.width - rootRect.left
            } else {
                // center
                left = triggerRect.left - rootRect.left + (triggerRect.width - contentRect.width) / 2
            }
            let top = 0

            if (this.placement === 'top') {
                top = triggerRect.top - rootRect.top - contentRect.height - this.offset
            } else {
                top = triggerRect.bottom - rootRect.top + this.offset
            }

            this.contentStyle = {
                top: `${top}px`,
                left: `${left}px`,
                zIndex: this.zIndex,
            }
        },
        onGlobalPointerDown(e) {
            // Close only if click/tap is outside the popover root.
            if (!this.$refs.root) return
            if (!this.$refs.root.contains(e.target)) this.close()
        },
        addGlobalListeners() {
            // Use pointer events if available; otherwise fall back to click.
            document.addEventListener('mousedown', this.onGlobalPointerDown)
            document.addEventListener('touchstart', this.onGlobalPointerDown, { passive: true })
            window.addEventListener('resize', this.updatePosition)
            window.addEventListener('scroll', this.onScroll, true)
        },
        removeGlobalListeners() {
            document.removeEventListener('mousedown', this.onGlobalPointerDown)
            document.removeEventListener('touchstart', this.onGlobalPointerDown)
            window.removeEventListener('resize', this.updatePosition)
            window.removeEventListener('scroll', this.onScroll, true)
        },
        onScroll() {
            // Recompute position while open (especially important if the page scrolls).
            if (this.open) this.updatePosition()
        },
        onKeyDown(e) {
            if (!this.open) return
            if (e && e.key === 'Escape') this.close()
        },
    },
}
</script>

<style scoped>
.popover {
    position: relative;
    display: flex;
}

.popover__trigger {
    display: inline-flex;
    cursor: pointer;
}

.popover[data-open="true"] .popover__trigger {
    opacity: 0.5;
}

.popover__content {
    width: max-content;
    position: absolute;
    background: var(--background-white);
    border: 1px solid var(--border-primary);
    border-radius: var(--radius-md);
    padding: var(--spacing-sm);
    box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
    animation: fadeIn 0.2s ease-in;
}

.popover__content > .button__close {
    position: absolute;
    top: var(--spacing-sm);
    right: var(--spacing-sm);
}

@keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}
</style>